import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface SessionCostRow {
  id: string;
  created_at: string;
  provider: string;
  model: string | null;
  operation: string;
  tokens_in: number | null;
  tokens_out: number | null;
  audio_duration_seconds: number | null;
  estimated_cost_usd: number | null;
  session_id: string | null;
}

interface SessionCostTableProps {
  rows: SessionCostRow[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  providerFilter: string | null;
  onProviderFilterChange: (provider: string | null) => void;
  isLoading: boolean;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTokens(tokensIn: number | null, tokensOut: number | null): string {
  if (tokensIn == null && tokensOut == null) return '—';
  return `${(tokensIn || 0).toLocaleString()} / ${(tokensOut || 0).toLocaleString()}`;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

export const SessionCostTable: React.FC<SessionCostTableProps> = ({
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  providerFilter,
  onProviderFilterChange,
  isLoading,
}) => {
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700">
          API Call Log
        </h3>
        <div className="flex gap-1">
          {(['all', 'openai', 'assemblyai'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() =>
                onProviderFilterChange(opt === 'all' ? null : opt)
              }
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                (opt === 'all' && !providerFilter) ||
                opt === providerFilter
                  ? 'bg-sky-100 text-sky-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {opt === 'all' ? 'All' : opt === 'openai' ? 'OpenAI' : 'AssemblyAI'}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Date</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Provider</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Model</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Operation</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Tokens (in/out)</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Duration</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">Cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-4 py-2.5">
                      <div className="h-3 bg-gray-100 rounded w-16" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="text-center py-8 text-gray-400"
                >
                  No API usage data yet
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        row.provider === 'openai'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-green-50 text-green-700'
                      }`}
                    >
                      {row.provider === 'openai' ? 'OpenAI' : 'AAI'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 font-mono text-[10px]">
                    {row.model || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">
                    {row.operation}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600 font-mono">
                    {row.provider === 'openai'
                      ? formatTokens(row.tokens_in, row.tokens_out)
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">
                    {row.provider === 'assemblyai'
                      ? formatDuration(row.audio_duration_seconds)
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-900 font-medium">
                    ${(row.estimated_cost_usd || 0).toFixed(4)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
          <span>
            Showing {page * pageSize + 1}–
            {Math.min((page + 1) * pageSize, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
